import { Component, OnInit, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import {
  LucideAngularModule,
  MailIcon,
  PhoneIcon,
  ShieldIcon,
  CheckCircleIcon,
  RecycleIcon,
  DollarSignIcon,
  LeafIcon,
  BatteryIcon,
} from 'lucide-angular';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'allas-reciclaje-baterias',
  imports: [CommonModule, MainLayout, LucideAngularModule],
  template: `<allas-main-layout>
    <div class="min-h-screen bg-white">
      <!-- Hero Section -->
      <section class="bg-white py-12 lg:py-20">
        <div class="container mx-auto px-4">
          <div class="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div class="order-2 lg:order-1">
              <img
                src="/servicios/reciclaje-baterias/reciclar-bateria.webp"
                alt="Servicio de reciclaje de baterías"
                class="rounded-lg w-full h-auto"
              />
            </div>
            <div class="order-1 lg:order-2 space-y-6">
              <h1
                class="text-3xl lg:text-5xl font-bold text-gray-900 leading-tight"
              >
                RECICLAJE RESPONSABLE DE BATERÍAS
              </h1>
              <p class="text-lg text-gray-600 leading-relaxed">
                Contribuye al cuidado del medio ambiente con nuestro servicio de
                reciclaje de baterías. Además, obtén un descuento especial en tu
                nueva batería.
              </p>
              <p class="text-sm text-gray-500">
                *Descuento aplicable al dejar tu batería usada con nosotros.
              </p>
              <button
                class="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg font-semibold rounded"
              >
                Encuentra tu Tienda
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Beneficios del Reciclaje -->
      <section class="bg-gray-50 py-16">
        <div class="container mx-auto px-4">
          <div class="text-center mb-12">
            <h2 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              BENEFICIOS DEL RECICLAJE DE BATERÍAS
            </h2>
            <p class="text-lg text-gray-600 max-w-3xl mx-auto">
              Al reciclar tu batería usada con nosotros, no solo cuidas el medio
              ambiente, sino que también obtienes ventajas económicas.
            </p>
          </div>

          <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div class="text-center space-y-4">
              <div
                class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto"
              >
                <lucide-icon
                  [name]="recycleIcon"
                  class="w-8 h-8 text-green-600"
                />
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                RECICLAJE SEGURO
              </h3>
              <p class="text-gray-600 text-sm">
                Procesamos tu batería usada de manera responsable y ecológica.
              </p>
            </div>

            <div class="text-center space-y-4">
              <div
                class="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto"
              >
                <lucide-icon
                  [name]="dollarSignIcon"
                  class="w-8 h-8 text-blue-600"
                />
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                DESCUENTO ESPECIAL
              </h3>
              <p class="text-gray-600 text-sm">
                Obtén un descuento en la compra de tu nueva batería.
              </p>
            </div>

            <div class="text-center space-y-4">
              <div
                class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto"
              >
                <lucide-icon [name]="leafIcon" class="w-8 h-8 text-green-600" />
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                CUIDADO AMBIENTAL
              </h3>
              <p class="text-gray-600 text-sm">
                Contribuyes a la protección del medio ambiente.
              </p>
            </div>

            <div class="text-center space-y-4">
              <div
                class="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto"
              >
                <lucide-icon
                  [name]="batteryIcon"
                  class="w-8 h-8 text-orange-600"
                />
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                PROCESO RÁPIDO
              </h3>
              <p class="text-gray-600 text-sm">
                Intercambio rápido y sencillo en cualquiera de nuestras tiendas.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Cómo Funciona -->
      <section class="bg-white py-16">
        <div class="container mx-auto px-4">
          <div class="text-center mb-12">
            <h2 class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              ¿CÓMO FUNCIONA NUESTRO SERVICIO?
            </h2>
          </div>

          <div class="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div class="text-center space-y-4">
              <div
                class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto"
              >
                <span class="text-2xl font-bold text-red-600">1</span>
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                TRAE TU BATERÍA USADA
              </h3>
              <p class="text-gray-600">
                Lleva tu batería usada a cualquiera de nuestras 12 sucursales.
              </p>
            </div>

            <div class="text-center space-y-4">
              <div
                class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto"
              >
                <span class="text-2xl font-bold text-red-600">2</span>
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                ELIGE TU NUEVA BATERÍA
              </h3>
              <p class="text-gray-600">
                Selecciona la batería nueva que mejor se adapte a tu vehículo.
              </p>
            </div>

            <div class="text-center space-y-4">
              <div
                class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto"
              >
                <span class="text-2xl font-bold text-red-600">3</span>
              </div>
              <h3 class="text-xl font-semibold text-gray-900">
                OBTÉN TU DESCUENTO
              </h3>
              <p class="text-gray-600">
                Recibe automáticamente tu descuento por el reciclaje
                responsable.
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Imagen Central del Reciclaje -->
      <section class="bg-gray-50 py-16">
        <div class="container mx-auto px-4">
          <div class="max-w-4xl mx-auto text-center">
            <img
              src="/servicios/reciclaje-baterias/retirar-bateria.webp"
              alt="Reciclaje de baterías ecológico"
              class="rounded-lg mx-auto mb-8 w-full max-w-4xl"
            />
            <h3 class="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              COMPROMISO CON EL MEDIO AMBIENTE
            </h3>
            <p class="text-lg text-gray-600 max-w-2xl mx-auto">
              En Allas Repuestos nos comprometemos con el reciclaje responsable
              de baterías, contribuyendo a un futuro más sostenible para todos.
            </p>
          </div>
        </div>
      </section>
    </div>
  </allas-main-layout>`,
})
export class ReciclajeBaterias implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);

  readonly mailIcon = MailIcon;
  readonly phoneIcon = PhoneIcon;
  readonly shieldIcon = ShieldIcon;
  readonly checkCircleIcon = CheckCircleIcon;
  readonly recycleIcon = RecycleIcon;
  readonly dollarSignIcon = DollarSignIcon;
  readonly leafIcon = LeafIcon;
  readonly batteryIcon = BatteryIcon;

  ngOnInit() {
    this.setMetaTags();
  }

  private setMetaTags() {
    const pageTitle = 'Reciclaje de Baterías | Allas Repuestos';
    const description =
      'Recicla tu batería usada y obtén descuento en tu nueva batería. Servicio ecológico y responsable en Honduras con envío a domicilio.';
    const keywords =
      'reciclaje baterías, baterías usadas, descuento baterías, reciclaje ecológico, Allas Repuestos, Honduras, envío a domicilio, baterías automotrices';
    const imageUrl = 'https://www.allasrepuestos.com/logos/isotipo.webp';

    // --- Título de la página ---
    this.title.setTitle(pageTitle);

    // --- Meta Descripción ---
    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    // --- Palabras Clave ---
    this.meta.updateTag({
      name: 'keywords',
      content: keywords,
    });

    // --- Open Graph Tags ---
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({
      property: 'og:image:alt',
      content: 'Logo de Allas Repuestos',
    });

    // --- Twitter Tags ---
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });

    // --- Icono del sitio (favicon / logo principal) ---
    this.meta.updateTag({
      name: 'icon',
      content: imageUrl,
    });
  }

  openWhatsApp() {
    window.open('https://wa.me/50495089953', '_blank');
  }

  openEmail() {
    window.open('mailto:ventas@allasrepuestos.com', '_blank');
  }
}
